export const TEMPERATURE_UNITS = [
  { key: 'f', label: 'F' },
  { key: 'c', label: 'C' },
];

export const WIND_SPEED_UNITS = [
  { key: 'mph', label: 'mph' },
  { key: 'ms', label: 'm/s' },
];

export const PRECIPITATION_UNITS = [
  { key: 'in', label: 'in' },
  { key: 'mm', label: 'mm' },
];

export const PRESSURE_UNITS = [
  { key: 'inhg', label: 'inHg' },
  { key: 'hpa', label: 'hPa' },
];

export const DISTANCE_UNITS = [
  { key: 'mi', label: 'mi' },
  { key: 'km', label: 'km' },
];

const METERS_PER_SECOND_TO_MILES_PER_HOUR = 2.2369362920544;
const MILLIMETERS_PER_INCH = 25.4;
const HECTOPASCALS_PER_INCH_OF_MERCURY = 33.863886666667;
const KILOMETERS_PER_MILE = 1.609344;
const HEAT_INDEX_MIN_TEMP_F = 80;
const HEAT_INDEX_MIN_HUMIDITY_PCT = 40;
const WIND_CHILL_MAX_TEMP_F = 50;
const WIND_CHILL_MIN_WIND_MPH = 3;
const WIND_METRIC_KEYS = new Set([
  'wind_avg_ms',
  'wind_gust_ms',
  'wind_lull_ms',
  'wind_ms',
  'wind_speed',
]);
const PRECIPITATION_METRIC_KEYS = new Set([
  'rain',
  'rain_mm',
  'rain_interval_mm',
  'nearcast_rain_mm',
  'local_day_rain_mm',
  'local_day_nearcast_rain_mm',
]);

export function toNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function formatUnitValue(value, suffix, decimals) {
  if (value === null || value === undefined || value === '') {
    return 'n/a';
  }

  const numericValue = typeof value === 'number' ? value : toNumber(value);

  if (numericValue === null) {
    return `${String(value)}${suffix}`;
  }

  if (Number.isInteger(numericValue) || decimals === 0) {
    return `${numericValue.toFixed(decimals)}${suffix}`;
  }

  return `${numericValue.toFixed(decimals).replace(/(\.\d)0$/, '$1')}${suffix}`;
}

export function getTemperatureSuffix(temperatureUnit = 'c') {
  return temperatureUnit === 'f' ? ' F' : ' C';
}

export function convertTemperatureValue(value, temperatureUnit = 'c') {
  const numericValue = toNumber(value);

  if (numericValue === null) {
    return value;
  }

  return temperatureUnit === 'f'
    ? ((numericValue * 9) / 5) + 32
    : numericValue;
}

export function formatTemperatureValue(value, temperatureUnit = 'c', decimals = 1) {
  return formatUnitValue(
    convertTemperatureValue(value, temperatureUnit),
    getTemperatureSuffix(temperatureUnit),
    decimals,
  );
}

function celsiusToFahrenheit(valueC) {
  return ((valueC * 9) / 5) + 32;
}

function fahrenheitToCelsius(valueF) {
  return ((valueF - 32) * 5) / 9;
}

function toWeatherNumber(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  return toNumber(value);
}

function calculateHeatIndexF(temperatureF, humidityPct) {
  let heatIndex =
    -42.379 +
    (2.04901523 * temperatureF) +
    (10.14333127 * humidityPct) -
    (0.22475541 * temperatureF * humidityPct) -
    (0.00683783 * temperatureF * temperatureF) -
    (0.05481717 * humidityPct * humidityPct) +
    (0.00122874 * temperatureF * temperatureF * humidityPct) +
    (0.00085282 * temperatureF * humidityPct * humidityPct) -
    (0.00000199 * temperatureF * temperatureF * humidityPct * humidityPct);

  if (humidityPct < 13 && temperatureF > 80 && temperatureF < 112) {
    heatIndex -= ((13 - humidityPct) / 4) *
      Math.sqrt((17 - Math.abs(temperatureF - 95)) / 17);
  } else if (humidityPct > 85 && temperatureF > 80 && temperatureF < 87) {
    heatIndex += ((humidityPct - 85) / 10) * ((87 - temperatureF) / 5);
  }

  return heatIndex;
}

function calculateWindChillF(temperatureF, windMph) {
  const windFactor = windMph ** 0.16;

  return 35.74 +
    (0.6215 * temperatureF) -
    (35.75 * windFactor) +
    (0.4275 * temperatureF * windFactor);
}

export function calculateFeelsLikeTemperatureC(
  airTempC,
  relativeHumidityPct,
  windAvgMs,
) {
  const temperatureC = toWeatherNumber(airTempC);

  if (temperatureC === null) {
    return null;
  }

  const temperatureF = celsiusToFahrenheit(temperatureC);

  if (temperatureF >= HEAT_INDEX_MIN_TEMP_F) {
    const humidityPct = toWeatherNumber(relativeHumidityPct);

    if (humidityPct === null) {
      return null;
    }

    return humidityPct >= HEAT_INDEX_MIN_HUMIDITY_PCT
      ? fahrenheitToCelsius(calculateHeatIndexF(temperatureF, humidityPct))
      : temperatureC;
  }

  if (temperatureF <= WIND_CHILL_MAX_TEMP_F) {
    const windMs = toWeatherNumber(windAvgMs);

    if (windMs === null) {
      return null;
    }

    const windMph = windMs * METERS_PER_SECOND_TO_MILES_PER_HOUR;

    return windMph >= WIND_CHILL_MIN_WIND_MPH
      ? fahrenheitToCelsius(calculateWindChillF(temperatureF, windMph))
      : temperatureC;
  }

  return temperatureC;
}

export function formatFeelsLikeTemperatureValue(
  airTempC,
  relativeHumidityPct,
  windAvgMs,
  temperatureUnit = 'c',
  decimals = 1,
) {
  const feelsLikeTemperatureC = calculateFeelsLikeTemperatureC(
    airTempC,
    relativeHumidityPct,
    windAvgMs,
  );

  if (feelsLikeTemperatureC === null) {
    return 'n/a';
  }

  return formatTemperatureValue(
    feelsLikeTemperatureC,
    temperatureUnit,
    decimals,
  );
}

export function getWindSpeedSuffix(windSpeedUnit = 'ms') {
  return windSpeedUnit === 'mph' ? ' mph' : ' m/s';
}

export function convertWindSpeedValue(value, windSpeedUnit = 'ms') {
  const numericValue = toNumber(value);

  if (numericValue === null) {
    return value;
  }

  return windSpeedUnit === 'mph'
    ? numericValue * METERS_PER_SECOND_TO_MILES_PER_HOUR
    : numericValue;
}

export function formatWindSpeedValue(value, windSpeedUnit = 'ms', decimals = 1) {
  return formatUnitValue(
    convertWindSpeedValue(value, windSpeedUnit),
    getWindSpeedSuffix(windSpeedUnit),
    decimals,
  );
}

export function getPrecipitationSuffix(precipitationUnit = 'mm') {
  return precipitationUnit === 'in' ? ' in' : ' mm';
}

export function getPrecipitationDecimals(precipitationUnit = 'mm', decimals = 1) {
  return precipitationUnit === 'in'
    ? Math.max(decimals, 2)
    : decimals;
}

export function convertPrecipitationValue(value, precipitationUnit = 'mm') {
  const numericValue = toNumber(value);

  if (numericValue === null) {
    return value;
  }

  return precipitationUnit === 'in'
    ? numericValue / MILLIMETERS_PER_INCH
    : numericValue;
}

export function formatPrecipitationValue(value, precipitationUnit = 'mm', decimals = 1) {
  return formatUnitValue(
    convertPrecipitationValue(value, precipitationUnit),
    getPrecipitationSuffix(precipitationUnit),
    getPrecipitationDecimals(precipitationUnit, decimals),
  );
}

export function getPressureSuffix(pressureUnit = 'hpa') {
  return pressureUnit === 'inhg' ? ' inHg' : ' hPa';
}

export function getPressureDecimals(pressureUnit = 'hpa', decimals = 1) {
  return pressureUnit === 'inhg'
    ? Math.max(decimals, 2)
    : decimals;
}

export function convertPressureValue(value, pressureUnit = 'hpa') {
  const numericValue = toNumber(value);

  if (numericValue === null) {
    return value;
  }

  return pressureUnit === 'inhg'
    ? numericValue / HECTOPASCALS_PER_INCH_OF_MERCURY
    : numericValue;
}

export function formatPressureValue(value, pressureUnit = 'hpa', decimals = 1) {
  return formatUnitValue(
    convertPressureValue(value, pressureUnit),
    getPressureSuffix(pressureUnit),
    getPressureDecimals(pressureUnit, decimals),
  );
}

export function getDistanceSuffix(distanceUnit = 'km') {
  return distanceUnit === 'mi' ? ' mi' : ' km';
}

export function convertDistanceValue(value, distanceUnit = 'km') {
  const numericValue = toNumber(value);

  if (numericValue === null) {
    return value;
  }

  return distanceUnit === 'mi'
    ? numericValue / KILOMETERS_PER_MILE
    : numericValue;
}

export function formatDistanceValue(value, distanceUnit = 'km', decimals = 1) {
  return formatUnitValue(
    convertDistanceValue(value, distanceUnit),
    getDistanceSuffix(distanceUnit),
    decimals,
  );
}

export function isWindMetricKey(key) {
  return WIND_METRIC_KEYS.has(key);
}

export function isPrecipitationMetricKey(key) {
  return PRECIPITATION_METRIC_KEYS.has(key);
}
